// Application tracker (spec §7–§11): the bank's review, the Key Fact
// Statement, documents, insurance — then accept & disburse.
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheck, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import {
  acknowledgeKfs,
  cancelApplication,
  disburseLoan,
  getKeyFactStatement,
  getLoan,
  setInsurance,
  submitDocuments,
} from '@/api/loans';
import { ApiError } from '@/api/client';
import type { KeyFactStatement, LoanRecord } from '@/types/loan.types';
import { productById } from '@/mock/loanData';
import { formatLong } from '@/mock/clock';
import Loader from '@/components/ui/Loader/Loader';
import Button from '@/components/ui/Button/Button';
import { ErrorLine, LoanStatusChip, inr } from './loanShared';

const ApplicationView = () => {
  const { loanId = '' } = useParams();
  const navigate = useNavigate();
  const [loan, setLoan] = useState<LoanRecord | null>(null);
  const [kfs, setKfs] = useState<KeyFactStatement | null>(null);
  const [docs, setDocs] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    getLoan(loanId)
      .then(async (l) => {
        setLoan(l);
        setDocs(l.documentsUploaded);
        if (['underReview', 'approved'].includes(l.state)) {
          setKfs(await getKeyFactStatement(l.id).catch(() => null));
        }
        if (['coolingOff', 'disbursed', 'active', 'unutilised', 'moratorium'].includes(l.state)) {
          navigate(`/borrow/loans/${l.id}`, { replace: true });
        }
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load the application.'));
  }, [loanId, navigate]);

  useEffect(load, [load]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The action failed.');
    } finally {
      setBusy(false);
    }
  };

  if (!loan) {
    return (
      <div className="nova-loans-page">
        {error ? <ErrorLine message={error} /> : <Loader rows={4} />}
      </div>
    );
  }

  const product = productById(loan.productId);
  const missingDocs = product.documents.filter((d) => !docs.includes(d));

  const toggleDoc = (doc: string) => {
    const next = docs.includes(doc) ? docs.filter((x) => x !== doc) : [...docs, doc];
    setDocs(next);
    void run(() => submitDocuments(loan.id, next));
  };

  return (
    <div className="nova-loans-page">
      <div className="nova-loans-header">
        <button type="button" className="nova-icon-btn" title="Back" aria-label="Back" onClick={() => navigate('/borrow')}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>{product.name}</h2>
      </div>

      <div className="nova-loan-cardhead">
        <span>
          <strong>{inr(loan.amountPaise)}</strong>
          <span className="nova-loan-row-sub"> Applied {formatLong(loan.appliedAt)}</span>
        </span>
        <LoanStatusChip state={loan.state} />
      </div>

      {loan.state === 'rejected' && (
        <div className="nova-loan-panel is-bad">
          <p>
            <FontAwesomeIcon icon={faCircleXmark} /> This application was not approved
            {loan.rejectReason ? `: ${loan.rejectReason}` : '.'}
          </p>
          <Button variant="outline" onClick={() => navigate('/borrow')}>Back to loans</Button>
        </div>
      )}

      {(loan.state === 'underReview' || loan.state === 'approved') && (
        <>
          <div className="nova-loan-panel">
            <h3 className="nova-loan-section">Where it stands</h3>
            <ol className="nova-loan-tracker">
              <li className="is-done"><FontAwesomeIcon icon={faCheck} /> Application submitted</li>
              <li className={loan.state === 'approved' ? 'is-done' : 'is-current'}>
                <FontAwesomeIcon icon={faCheck} /> Bank review
              </li>
              <li className={loan.state === 'approved' ? 'is-current' : ''}>Approved</li>
              <li>Disbursal</li>
            </ol>
            <Button variant="outline" onClick={load} disabled={busy}>Refresh status</Button>
          </div>

          {kfs && (
            <div className="nova-loan-panel">
              <h3 className="nova-loan-section">Key Fact Statement</h3>
              <div className="nova-loan-kfs-grid">
                {kfs.sections.map((s) => (
                  <div key={s.title} className="nova-loan-kfs">
                    <strong>{s.title}</strong>
                    {s.rows.map((r) => (
                      <div key={r.label} className="nova-loan-review-row">
                        <span>{r.label}</span>
                        <strong>{r.value}</strong>
                      </div>
                    ))}
                    {s.bullets?.map((b) => (
                      <p key={b} className="nova-loan-note">• {b}</p>
                    ))}
                  </div>
                ))}
              </div>
              {!loan.kfsAccepted ? (
                <Button onClick={() => run(() => acknowledgeKfs(loan.id))} isLoading={busy}>
                  I have read the Key Fact Statement
                </Button>
              ) : (
                <p className="nova-loan-ok"><FontAwesomeIcon icon={faCheck} /> Key Fact Statement acknowledged</p>
              )}
            </div>
          )}

          {product.documents.length > 0 && loan.state === 'underReview' && (
            <div className="nova-loan-panel">
              <h3 className="nova-loan-section">Documents</h3>
              {missingDocs.length > 0 && (
                <p className="nova-loan-note">Tick each document as you upload it.</p>
              )}
              {product.documents.map((doc) => (
                <label key={doc} className="nova-loan-holding">
                  <input type="checkbox" checked={docs.includes(doc)} onChange={() => toggleDoc(doc)} />
                  <span className="nova-loan-holding-text"><strong>{doc}</strong></span>
                </label>
              ))}
            </div>
          )}

          {loan.productId === 'PERSONAL' && (
            <div className="nova-loan-panel">
              <label className="nova-loan-consent">
                <input
                  type="checkbox"
                  checked={loan.insuranceOpted}
                  onChange={(e) => run(() => setInsurance(loan.id, e.target.checked))}
                />
                <span>Loan insurance{loan.insurancePaise > 0 ? ` — ${inr(loan.insurancePaise)}` : ''}</span>
              </label>
            </div>
          )}

          {loan.state === 'approved' && (
            <div className="nova-loan-panel">
              <h3 className="nova-loan-section">Approved</h3>
              <p className="nova-loan-note">
                Accept to disburse. You have {kfs?.coolingOffDays ?? 3} day(s) after disbursal to change your mind
                — return the principal plus interest for the days held and the loan closes.
              </p>
              <Button
                onClick={() => run(() => disburseLoan(loan.id))}
                isLoading={busy}
                disabled={!loan.kfsAccepted}
                title={loan.kfsAccepted ? undefined : 'Read the Key Fact Statement first'}
              >
                Accept & disburse
              </Button>
            </div>
          )}

          <button type="button" className="nova-loan-link is-danger" onClick={() => run(() => cancelApplication(loan.id))}>
            Cancel this application
          </button>
        </>
      )}

      {loan.state === 'cancelled' && (
        <div className="nova-loan-panel">
          <p>This application was cancelled.</p>
          <Button variant="outline" onClick={() => navigate('/borrow')}>Back to loans</Button>
        </div>
      )}

      <ErrorLine message={error} />
    </div>
  );
};

export default ApplicationView;
